import React, { useState, useEffect, useRef } from "react";
import OpenAI from "openai"; // Import OpenAI library
import ReactMarkdown from "react-markdown"; // Import react-markdown
import "./Chatbot.css";

// Simple chat bubble icon (replace with a proper icon library if available)
const ChatIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    width="24px"
    height="24px"
  >
    <path d="M0 0h24v24H0V0z" fill="none" />
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    width="24px"
    height="24px"
  >
    <path d="M0 0h24v24H0V0z" fill="none" />
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
  </svg>
);

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const chatboxRef = useRef(null);
  const openai = useRef(null); // To store the OpenAI client instance
  const modelName = "gpt-4o" || "openai/o4-mini";
  const igelServiceInfo = `
  Vous êtes un assistant virtuel IA pour GI (Généralité informatique), une entreprise experte en installations caméras de surveillance, reseaux et maintenance située à Garoua, Cameroun.
  Le slogan de GI est "Travaillons ensemble pour votre securite."
  GI accompagne les particuliers et les entreprises.

  Services principaux :
  - Installations de réseaux.
  - Systèmes de sécurité : vidéosurveillance, contrôle d'accès, réseaux de sécurité intégrés. Installation, maintenance et réparation de caméras de sécurité.
  - Programmes de maintenance préventive.
  - ventes des fournitures et équipement reseaux

  Contactez-nous au numéro de téléphone : (+237) 691891212
  ou via notre site web : https://generalite-informatique.vercel.app/ventes
  vous pouvez consultez nos produits sur : https://generalite-informatique.vercel.app/produits


  L'équipe est composée d'ingénieurs et techniciens experts, axés sur la qualité, la sécurité, l'innovation et le développement durable.
  Répondez aux questions des clients en FRANÇAIS. Soyez aimable et concis. Si une question sort du cadre des services d'GI, indiquez poliment que vous ne pouvez aider que sur des sujets liés à GI.
  Commencez la conversation en saluant l'utilisateur et en vous présentant brièvement.
  `;

  useEffect(() => {
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    const endpoint =
      "https://models.inference.ai.azure.com" ||
      "https://models.github.ai/inference";

    if (apiKey) {
      openai.current = new OpenAI({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true,
        baseURL: endpoint,
      });
    } else {
      console.error(
        "OpenAI API key not found. Make sure it's set in .env and the app is rebuilt if necessary."
      );
    }
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0 && openai.current) {
      // Check if openai client is initialized
      setMessages([
        {
          sender: "bot",
          text: "Bonjour ! Je suis l'assistant virtuel GI. Comment puis-je vous aider aujourd'hui concernant nos services électriques, de climatisation, ou de sécurité ?",
        },
      ]);
    } else if (isOpen && messages.length === 0 && !openai.current) {
      setMessages([
        {
          sender: "bot",
          text: "Désolé, le service d'assistance n'est pas correctement configuré (clé API manquante). Veuillez contacter l'administrateur.",
        },
      ]);
    }
  }, [isOpen, messages.length]); // Added messages.length to dependency array

  useEffect(() => {
    // Scroll to bottom of chatbox when new messages are added
    if (chatboxRef.current) {
      chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
    }
  }, [messages]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const fetchOpenAIResponse = async (userMessageText) => {
    setIsLoading(true);

    if (!openai.current) {
      console.error("OpenAI client not initialized. API key might be missing.");
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          text: "Désolé, je ne peux pas me connecter au service d'assistance pour le moment. Problème de configuration.",
        },
      ]);
      setIsLoading(false);
      return;
    }

    const systemMessage = {
      role: "system",
      content: igelServiceInfo,
    };

    const conversationHistory = messages
      .filter((msg) => msg.sender === "user" || msg.sender === "bot")
      .map((msg) => ({
        role: msg.sender === "user" ? "user" : "assistant",
        content: msg.text,
      }));

    // Add a placeholder for the streaming bot message
    setMessages((prevMessages) => [
      ...prevMessages,
     { sender: "bot", text: "" },
    ]);

    try {
      const stream = await openai.current.chat.completions.create({
        model: modelName,
        messages: [
          systemMessage,
          ...conversationHistory,
          { role: "user", content: userMessageText },
        ],
        temperature: 0.7,
        stream: true, // Enable streaming
      });

      let streamedResponse = "";
      for await (const chunk of stream) {
        const content = chunk.choices[0]?.delta?.content || "";
        if (content) {
          streamedResponse += content;
          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1].sender="bot"
            updatedMessages[updatedMessages.length - 1].text = streamedResponse;
            return updatedMessages;
          });
        }
      }

      if (streamedResponse === "") {
        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages];
          updatedMessages[updatedMessages.length - 1].text =
            "Je n'ai pas bien compris, pourriez-vous reformuler ?";
          return updatedMessages;
        });
      }
    } catch (error) {
      console.error("Error fetching OpenAI response:", error);
      let errorMessage =
        "Oups, une erreur s'est produite. Veuillez réessayer plus tard.";
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = `Erreur OpenAI: ${error.response.data.error.message.replace(
          /`/g,
          "\\`"
        )}`;
      } else if (error.message) {
        errorMessage = error.message.replace(/`/g, "\\`");
      }

      setMessages((prevMessages) => {
        // Remove the placeholder message if an error occurs before any stream data
        const lastMessage = prevMessages[prevMessages.length - 1];
        if (
          lastMessage &&
          lastMessage.sender === "bot" &&
          lastMessage.text === ""
        ) {
          prevMessages.pop();
        }
        return [...prevMessages, { sender: "bot", text: errorMessage }];
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (inputValue.trim() === "") return;

    const newUserMessage = { sender: "user", text: inputValue };
    // Add user message to state first
    setMessages((prevMessages) => [...prevMessages, newUserMessage]);
    const currentInput = inputValue; // Capture current input before clearing
    setInputValue("");

    await fetchOpenAIResponse(currentInput); // Pass the captured input
  };

  return (
    <>
      <button
        className="chatbot-fab"
        onClick={toggleChat}
        aria-label="Ouvrir le chat"
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </button>

      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <h3>Assistance GI</h3>
            <button
              onClick={toggleChat}
              className="chatbot-close-btn"
              aria-label="Fermer le chat"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="chatbot-messages" ref={chatboxRef}>
            {messages.filter((msg)=>msg.text!=="").map((msg, index) => (
              <div key={index} className={`message ${msg.sender}`}>
                {/* Fixed template literal for className */}
                {msg.sender === "bot" ? (
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                ) : (
                  msg.text
                )}
              </div>
            ))}
            {isLoading && (
              <div className="message bot typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
          </div>
          <form className="chatbot-input-form" onSubmit={handleSendMessage}>
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              placeholder="Posez votre question..."
              aria-label="Votre message"
            />
            <button
              type="submit"
              disabled={isLoading || !openai.current}
              aria-label="Envoyer le message"
            >
              Envoyer
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default Chatbot;
